/**
 * Pure helpers for selective question import (Opt 2): ship only the selected
 * questions' images instead of the whole file's image map. Kept out of the
 * React component so they're unit-testable.
 */

export type ImportImagesMap = Record<string, { data: string; mimeType: string; url?: string }> | undefined;

/** Collect the image URLs a question references across every image-bearing field. */
export function collectImageUrls(q: Record<string, unknown>, into: Set<string>): void {
  const add = (v: unknown) => { if (typeof v === 'string' && v) into.add(v); };
  add(q.questionDiagramUrl);
  add(q.solutionImageUrl);
  const opts = q.options as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(opts)) for (const o of opts) add(o?.imageUrl);
  const parts = q.parts as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(parts)) for (const p of parts) {
    add(p?.diagramUrl);
    const popts = p?.options as Array<Record<string, unknown>> | undefined;
    if (Array.isArray(popts)) for (const o of popts) add(o?.imageUrl);
  }
}

/**
 * Reduce an images map to only what `questions` reference — matched by the
 * entry's `url` OR its key (payloads reference images either way). Preserves
 * keys, dedupes shared images. Returns undefined when nothing is referenced.
 */
export function reduceImagesForQuestions(
  questions: Record<string, unknown>[],
  images: ImportImagesMap,
): ImportImagesMap {
  if (!images) return undefined;
  const referenced = new Set<string>();
  for (const q of questions) collectImageUrls(q, referenced);
  const reduced: NonNullable<ImportImagesMap> = {};
  for (const key in images) {
    const url = images[key].url;
    if (referenced.has(key) || (url && referenced.has(url))) reduced[key] = images[key];
  }
  return Object.keys(reduced).length > 0 ? reduced : undefined;
}
